import { useMemo } from 'react'

import ClientSideTable from '@/components/Table/ClientSideTable'
import { TooltipCell } from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { isNumber } from '@/utils/helper'
import { isString, numberWithCommas } from '@/utils/stringHelper'
import { filterRange } from '@/utils/tableHelper'
import { Box, Flex, Text, Tooltip, VStack } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { CiCircleAlert } from 'react-icons/ci'
import { sumArray } from '@/utils/arrayHelper'

const columnHelper = createColumnHelper()

const ACCESSOR_TO_HEADER = {
  ref: 'Ref',
  subSystemIndex: 'Sub-System',
  modelNumber: 'Model',
  description: 'Description',
  quantity: 'Qty',
  currency: 'Currency',
  unitCost: 'Unit Cost (CUR)',
  masterUnitCost: 'Unit Cost (Master)',
  masterCurrency: 'Currency (Master)',
  masterUnit: 'UOM',
  masterSAPMaterialNumber: 'SAP Material Number',
  masterVendor: 'Vendor',
  masterSEN: 'SEN',
  masterArticleType: 'Article Type',
  masterMOQ: 'MOQ',
  masterDescription: 'Master Description',
  masterCtyOfOrigin: 'Country of Origin',
  validation: 'Validation',
}

const OfferGCMaterialTable = ({ data = [] }) => {
  const subSystemFilterOptions = useMemo(
    () =>
      Array.from(new Set(data.map((item) => item.subSystemIndex))).map((index) => ({
        label: `${index}`,
        value: index,
      })),
    [data]
  )

  const columnDef = useMemo(
    () => [
      columnHelper.display({
        id: 'ref',
        size: 50,
        enableHiding: false,
        enableColumnFilter: false,
        header: ACCESSOR_TO_HEADER.ref,
        headerColor: 'black',
        cell: ({ row: { index } }) => <span>{index + 1}</span>,
      }),
      columnHelper.accessor('subSystemIndex', {
        header: ACCESSOR_TO_HEADER.subSystemIndex,
        enableColumnFilter: false,

        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('modelNumber', {
        header: ACCESSOR_TO_HEADER.modelNumber,
        cell: ({ getValue, row: { original } }) => {
          const modelNumber = getValue() ?? '(-)'
          const masterSEN = original?.masterSEN ?? '(-)'
          const masterArticleType = original?.masterArticleType ?? '(-)'
          const masterDiscontinued = original?.detail?.discontinued ?? false

          let highlightReason = ''

          let highlightColor = 'black'

          if (modelNumber !== '(-)') {
            //Check if model number is mapped to SEN / Article Type / SAP Material Number
            const formattedModelNumber = modelNumber.replace('BPZ:', '').trim()
            const isMappedToSEN = masterSEN.replace('BPZ:', '').trim() === formattedModelNumber
            const isMappedToArticleType = masterArticleType.replace('BPZ:', '').trim() === formattedModelNumber

            if (isMappedToSEN && modelNumber !== masterSEN) {
              highlightReason = 'Model number mapped to SEN but not same as master'
              highlightColor = 'red'
            } else if (isMappedToArticleType && modelNumber !== masterArticleType) {
              highlightReason = 'Model number mapped to Article Type but not same as master'
              highlightColor = 'red'
            }

            if (masterDiscontinued) {
              highlightReason += ' Model number mapped to discontinued master'
              highlightColor = 'orange'
            }
          }

          return <TooltipCell text={modelNumber} color={highlightColor} tooltip={highlightReason} />
        },
      }),
      columnHelper.accessor('description', {
        header: ACCESSOR_TO_HEADER.description,
        filterFn: (row, columnId, filterValue) => {
          const rowDescription = String(row.getValue(columnId) || '')

          return rowDescription.toLowerCase().includes(String(filterValue || '').toLowerCase())
        },
        cell: ({ getValue, row: { original, index } }) => {
          const description = getValue() || ''

          const masterDescription = original?.masterDescription || ''
          let highlightReason = ''

          if (masterDescription && description !== masterDescription) {
            highlightReason = 'Description not same as master'
          }

          return (
            <TooltipCell
              text={description && description !== '' ? description : '(-)'}
              color={highlightReason === '' ? 'black' : 'red'}
              tooltip={highlightReason}
            />
          )
        },
      }),
      columnHelper.accessor('quantity', {
        header: ACCESSOR_TO_HEADER.quantity,
        cell: ({ getValue, row: { original } }) => {
          const quantity = getValue()

          return <TooltipCell text={numberWithCommas(quantity)} />
        },
        filterFn: filterRange,
        meta: {
          filterVariant: FilterType.RANGE,
        },
      }),
      columnHelper.accessor('currency', {
        header: ACCESSOR_TO_HEADER.currency,
        cell: ({ getValue, row: { original } }) => {
          const currency = getValue()

          return <TooltipCell text={currency ? currency : '(-)'} />
        },
      }),
      columnHelper.accessor('unitCost', {
        header: ACCESSOR_TO_HEADER.unitCost,

        filterFn: filterRange,
        meta: {
          filterVariant: FilterType.RANGE,
        },

        cell: ({ getValue, row: { original } }) => {
          const unitCost = getValue() || 0
          const masterUnitCost = original?.masterUnitCost || 0

          let highlightReason = ''
          if (original?.masterCurrency && original?.currency !== original?.masterCurrency) {
            highlightReason = 'Currency not same as master'
          } else if (original?.masterUnitCost && unitCost !== masterUnitCost) {
            highlightReason = 'Unit cost not same as master'
          }

          return (
            <TooltipCell
              text={unitCost ? `$${numberWithCommas(unitCost)}` : '(-)'}
              color={highlightReason === '' ? 'black' : 'red'}
              tooltip={highlightReason}
            />
          )
        },
        footer: ({ table }) => {
          const totalHKD = sumArray(table.getFilteredRowModel().rows.map((row) => row?.original?.totalHKDCost || 0))
          return (
            <span>
              <b>Filtered Cost: </b>${numberWithCommas(totalHKD)}
            </span>
          )
        },
      }),
      columnHelper.accessor('masterCurrency', {
        header: ACCESSOR_TO_HEADER.masterCurrency,
        backgroundColor: '#d3dedd',
        filterFn: 'includesString',

        cell: ({ getValue }) => {
          return <TooltipCell text={getValue() || '(-)'} />
        },
      }),
      columnHelper.accessor('masterUnitCost', {
        header: ACCESSOR_TO_HEADER.masterUnitCost,
        backgroundColor: '#d3dedd',
        filterFn: filterRange,
        meta: {
          filterVariant: FilterType.RANGE,
        },
        cell: ({ getValue, row: { original } }) => {
          return (
            <Flex gap="1" alignItems={'center'}>
              {Array.isArray(original?.mappedDetails) && original.mappedDetails.length > 1 && (
                <Tooltip
                  label={
                    <VStack alignItems={'start'}>
                      {original.mappedDetails.map((detail, detailIndex) => {
                        const vendorName = detail?.vendor !== '' ? detail?.vendor : '(Vendor)'
                        return (
                          <Text key={`${detail.SEN}-${detailIndex}`}>
                            {vendorName} - {detail.SEN} - {detail.currency} {numberWithCommas(detail.unitCost)}
                          </Text>
                        )
                      })}
                    </VStack>
                  }
                  closeOnScroll
                >
                  <Box>
                    <CiCircleAlert size={'20'} />
                  </Box>
                </Tooltip>
              )}

              <TooltipCell text={`$${numberWithCommas(getValue()) || '(-)'}`} />
            </Flex>
          )
        },
      }),

      columnHelper.accessor('masterUnit', {
        header: ACCESSOR_TO_HEADER.masterUnit,
        filterFn: 'includesString',
        backgroundColor: '#d3dedd',
        cell: ({ getValue, row: { original } }) => {
          const MOQ = original?.masterMOQ || 0

          const displayValue = getValue() ? `${getValue()}${MOQ > 1 ? ` (MOQ: ${numberWithCommas(MOQ)})` : ''}` : `(-)`

          return <TooltipCell text={displayValue} />
        },
      }),
      columnHelper.accessor('masterSAPMaterialNumber', {
        header: ACCESSOR_TO_HEADER.masterSAPMaterialNumber,
        filterFn: 'includesString',
        backgroundColor: '#d3dedd',
        cell: ({ getValue }) => {
          return <TooltipCell text={getValue() || '(-)'} />
        },
      }),
      columnHelper.accessor('masterVendor', {
        header: ACCESSOR_TO_HEADER.masterVendor,
        filterFn: 'includesString',
        backgroundColor: '#d3dedd',
        cell: ({ getValue }) => {
          return <TooltipCell text={getValue() || '(-)'} />
        },
      }),
      columnHelper.accessor('masterSEN', {
        header: ACCESSOR_TO_HEADER.masterSEN,
        filterFn: 'includesString',
        backgroundColor: '#d3dedd',
        cell: ({ getValue, row: { original, index } }) => {
          const isActive = original.masterIsActive

          let highlightReason = ''

          if (!getValue()) {
            highlightReason = 'No SEN mapped'
          } else if (!isActive) {
            highlightReason = 'Mapped Detail is inactive'
          }

          return (
            <TooltipCell
              text={getValue() || '(-)'}
              color={highlightReason === '' ? 'black' : 'red'}
              tooltip={highlightReason}
            />
          )
        },
      }),
      columnHelper.accessor('masterArticleType', {
        header: ACCESSOR_TO_HEADER.masterArticleType,
        filterFn: 'includesString',
        backgroundColor: '#d3dedd',
        cell: ({ getValue }) => {
          return <TooltipCell text={getValue() || '(-)'} />
        },
      }),
      columnHelper.accessor('masterDescription', {
        header: ACCESSOR_TO_HEADER.masterDescription,

        backgroundColor: '#d3dedd',
        cell: ({ getValue }) => {
          return <TooltipCell text={getValue() || '(-)'} />
        },
      }),
      columnHelper.accessor('masterCtyOfOrigin', {
        header: ACCESSOR_TO_HEADER.masterCtyOfOrigin,
        filterFn: 'includesString',
        backgroundColor: '#d3dedd',
        cell: ({ getValue }) => {
          return <TooltipCell text={getValue() || '(-)'} />
        },
      }),
    ],
    []
  )

  const formatData = (data) => {
    return data.map((item, index) => {
      let validation = []
      const modelNumber = String(item.modelNumber || '')

      if (modelNumber && modelNumber !== '(-)') {
        //Check if model number is mapped to SEN / Article Type / SAP Material Number
        const formattedModelNumber = modelNumber.replace('BPZ:', '').trim()
        const isMappedToSEN =
          isString(item.masterSEN) && item.masterSEN.replace('BPZ:', '').trim() === formattedModelNumber
        const isMappedToArticleType =
          isString(item.masterArticleType) && item.masterArticleType.replace('BPZ:', '').trim() === formattedModelNumber

        if (isMappedToSEN && modelNumber !== item.masterSEN) {
          validation.push('Model number mapped to SEN but not same as master')
        } else if (isMappedToArticleType && modelNumber !== item.masterArticleType) {
          validation.push('Model number mapped to Article Type but not same as master')
        }
      }

      if (item?.masterDescription && item?.description !== item?.masterDescription) {
        validation.push('Description not same as master')
      }
      if (!item?.masterSEN) {
        validation.push('No SEN mapped')
      } else if (!item?.masterIsActive) {
        validation.push('Mapped Detail is inactive')
      }

      if (item?.masterCurrency && item?.currency !== item?.masterCurrency) {
        validation.push('Currency not same as master')
      }
      if (item?.masterUnitCost && item?.unitCost !== item?.masterUnitCost) {
        validation.push('Unit cost not same as master')
      }

      return {
        [ACCESSOR_TO_HEADER.subSystemIndex]: item.subSystemIndex,
        [ACCESSOR_TO_HEADER.ref]: index + 1,
        [ACCESSOR_TO_HEADER.modelNumber]: item.modelNumber,
        [ACCESSOR_TO_HEADER.description]: item.description,
        [ACCESSOR_TO_HEADER.quantity]: item.quantity,
        [ACCESSOR_TO_HEADER.currency]: item.currency,
        [ACCESSOR_TO_HEADER.unitCost]: `${numberWithCommas(item.unitCost)}`,
        [ACCESSOR_TO_HEADER.masterCurrency]: item.masterCurrency || '(-)',
        [ACCESSOR_TO_HEADER.masterUnitCost]: `${isNumber(item.masterUnitCost) ? numberWithCommas(item.masterUnitCost) : '(-)'}`,
        [ACCESSOR_TO_HEADER.masterUnit]: item.masterUnit || '(-)',
        [ACCESSOR_TO_HEADER.masterSAPMaterialNumber]: item.masterSAPMaterialNumber || '(-)',
        [ACCESSOR_TO_HEADER.masterSEN]: item.masterSEN || '(-)',
        [ACCESSOR_TO_HEADER.masterArticleType]: item.masterArticleType || '(-)',
        [ACCESSOR_TO_HEADER.masterMOQ]: item.masterMOQ || '(-)',
        [ACCESSOR_TO_HEADER.masterDescription]: item.masterDescription || '(-)',
        [ACCESSOR_TO_HEADER.masterCtyOfOrigin]: item.masterCtyOfOrigin || '(-)',
        [ACCESSOR_TO_HEADER.validation]: validation.length > 0 ? validation.join(', ') : '(-)',
      }
    })
  }

  const totalHKPrice = useMemo(() => {
    return data.reduce((acc, curr) => acc + (curr?.totalHKDCost || 0), 0)
  }, [data])

  return (
    <Flex direction={'column'} gap="2">
      <Flex gap="2">
        <Text as="b">Total Cost (HKD): </Text>
        <Text>${numberWithCommas(totalHKPrice)} </Text>
      </Flex>
      <Flex overflowX={'scroll'} direction={'column'}>
        <ClientSideTable
          tableWidth="100%"
          columnDef={columnDef}
          data={data}
          disabled
          canDownload
          downloadDataFormatter={formatData}
          showFooter
        />
      </Flex>
    </Flex>
  )
}

export default OfferGCMaterialTable
